import PropTypes from "prop-types";
import React, { Component, createRef } from "react";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography
} from "@material-ui/core";
import {
  Mic as MicIcon,
  CloudUpload as CloudUploadIcon,
  VideoCall as VideoCallIcon,
  MeetingRoom as MeetingRoomIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Memory as MemoryIcon,
  Assignment as AssignmentIcon
} from "@material-ui/icons";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { withAlert } from "react-alert";
import { withTranslation } from "react-i18next";
import moment from "moment";
import i18n from "../../utils/i18n";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import BackButton from "../../components/BackButton";
import Button from "../../waybee-ui/Button";
import NewFeatureBadge from "../../components/NewFeatureBadge";
import TeacherService from "../../services/TeacherService";
import StudentService from "../../services/StudentService";
import UserStore from "../../utils/UserStore";
import isClassDiaryDemo from "../../demo/isClassDiaryDemo";
import SmartMinutesService from "../../services/SmartMinutesService";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import {
  ACTION_STATUS,
  ATA_STATUS,
  buildPriorMeetingContext,
  buildSuggestedActions,
  collectActionsFromAtas,
  createDraftId,
  getActionStatus,
  isActionOpen,
  listPriorAtasForStudents,
  getAtaDraft,
  saveAtaDraft,
  syncAtaDraftToServer
} from "./ataDraftStore";
import { saveAtaAudio } from "./ataAudioStore";
import fileToBase64 from "./fileToBase64";
import {
  buildDemoStaffForClass,
  enrichStudentWithGuardians,
  mapTeacherToStaff,
  mergeStaffLists
} from "./demoParticipants";
import AtaActionsGrouped from "./AtaActionsGrouped";
import { theme as appTheme } from "../../assets/theme/StyledTheme";

function guardianResponsibilityLabel(responsibility) {
  if (responsibility === "pedagogico") {
    return i18n.t("smartMinutes.role.pedagogicalShort");
  }
  if (responsibility === "financeiro") {
    return i18n.t("smartMinutes.role.financialShort");
  }
  return null;
}

function formatGuardianChipLabel(guardian) {
  const role =
    (guardian && guardian.role) || i18n.t("smartMinutes.role.guardian");
  const name = (guardian && guardian.name) || "";
  const resp = guardianResponsibilityLabel(guardian && guardian.responsibility);
  if (resp) return `${role} · ${resp}: ${name}`;
  return `${role}: ${name}`;
}

const LoadContainer = styled.div`
  text-align: center;
  padding: 32px 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const EntryCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  min-height: 110px;
  padding: 14px;
  border-radius: 10px;
  border: 2px solid
    ${({ $active, theme }) => ($active ? theme.color.primary.dark : "#d0d0d0")};
  background: ${({ $active, theme }) =>
    $active ? theme.color.primary.light : "#fff"};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.color.primary.dark};
  }

  svg {
    color: ${({ theme }) => theme.color.primary.dark};
    font-size: 30px;
    margin-bottom: 6px;
  }
`;

const FilePickButton = styled(Button)`
  && {
    margin: 0;
    margin-top: 12px;
    min-width: 220px;
    padding: 8px 18px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.color.primary.dark};
    background-color: ${({ theme }) => theme.color.primary.main};
    color: #fff;
    font-weight: 600;
    box-shadow: 0 2px 6px ${({ theme }) => theme.color.primary.light};

    &:hover {
      background-color: ${({ theme }) => theme.color.primary.dark};
    }
  }
`;

const SoftDivider = styled.div`
  height: 1px;
  margin: 20px 0 8px;
  background: rgba(0, 48, 59, 0.1);
`;

const SectionHeader = styled.button`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const StudentRow = styled.div`
  border: 1px solid #e6ecee;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
  background: ${({ $selected, theme }) =>
    $selected ? theme.color.primary.light : "#fff"};
  border-color: ${({ $selected, theme }) =>
    $selected ? theme.color.primary.main : "#e6ecee"};
`;

const GuardianChip = styled(Chip)`
  && {
    height: 28px;
    max-width: 100%;
  }
`;

const SummaryLine = styled(Typography)`
  && {
    margin-top: 6px;
    color: #455a64;
    font-size: 13px;
    line-height: 1.45;
  }
`;

const BulkActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const BulkButton = styled.button`
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === "clear"
        ? "rgba(96, 118, 129, 0.35)"
        : theme.color.primary.main};
  background: ${({ $tone, theme }) =>
    $tone === "clear" ? "#fff" : theme.color.primary.light};
  color: ${({ $tone, theme }) =>
    $tone === "clear" ? "#607681" : theme.color.primary.dark};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ $tone, theme }) =>
      $tone === "clear" ? "#f5f7f8" : theme.color.primary.light};
    border-color: ${({ $tone, theme }) =>
      $tone === "clear" ? "#90a4ae" : theme.color.primary.dark};
  }
`;

const HistoryMeta = styled(Typography)`
  && {
    color: #607681;
    font-size: 13px;
  }
`;

const FormSplit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media (min-width: 960px) {
    display: grid;
    grid-template-columns: ${({ $hasSide }) =>
      $hasSide ? "minmax(0, 1.15fr) minmax(280px, 0.85fr)" : "minmax(0, 1fr)"};
    gap: 16px;
    align-items: start;
  }
`;

const StickySideCard = styled.div`
  @media (min-width: 960px) {
    position: sticky;
    top: 16px;
  }
`;

const PriorActionsScroll = styled.div`
  max-height: min(62vh, 560px);
  overflow: auto;
`;

const FocusLabel = styled.div`
  margin: 14px 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.primary.dark};
`;

const SummaryLineActions = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #607681;
`;

const HistorySectionLabel = styled.div`
  margin: 18px 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #546e7a;
`;

const MeetingToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 0 0 4px;
  padding: 8px 4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: #37474f;

  &:hover {
    background: ${({ theme }) => theme.color.primary.light};
  }

  .MuiSvgIcon-root {
    flex: 0 0 auto;
    font-size: 20px;
    color: #78909c;
  }
`;

const MeetingToggleTitle = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
`;

const MeetingToggleWhen = styled.span`
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  color: #78909c;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const MeetingToggleCount = styled.span`
  flex: 0 0 auto;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(0, 48, 59, 0.06);
  color: #607681;
  font-size: 11px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const ENTRY_UPLOAD = "upload_audio";
const ENTRY_MEET = "google_meet";
const ENTRY_PRESENCIAL = "presencial";

class SmartMinutes extends Component {
  fileInputRef = createRef();

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      students: [],
      staff: [],
      selectedRas: [],
      selectedGuardianIdsByRa: {},
      studentPresentByRa: {},
      selectedStaffIds: [],
      studentsExpanded: true,
      staffExpanded: true,
      priorHistoryOpenById: {},
      titleTouched: false,
      entryMode: ENTRY_UPLOAD,
      title: "",
      meetingAt: moment().format("YYYY-MM-DDTHH:mm"),
      notes: "",
      audioFileName: "",
      audioFile: null,
      classId: null,
      fromStudent: false,
      submitting: false
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  bootstrap = async () => {
    const { match, alert, t } = this.props;
    const { id: classIdParam, studentId } = match.params;
    let classId = classIdParam || localStorage.getItem("class") || "";

    this.setState({ loading: true });

    try {
      let students = [];
      let fromStudent = false;
      let selectedRas = [];
      let selectedGuardianIdsByRa = {};
      let studentPresentByRa = {};
      let title = t("smartMinutes.new.defaultTitle");

      const demoFallback = isClassDiaryDemo();

      if (studentId) {
        const student = await StudentService.getStudentById(studentId);
        // Preferir a turma do aluno (fluxo /student/:id) — localStorage pode ser de outra turma
        const studentClassId =
          student.classId ||
          student.idTurma ||
          (student.class && (student.class.id || student.class.classId)) ||
          null;
        if (studentClassId) {
          classId = String(studentClassId);
          try {
            localStorage.setItem("class", classId);
          } catch (e) {
            /* ignore */
          }
        }
        const enriched = {
          ...enrichStudentWithGuardians(
            {
              id: student.id || student.ra,
              ra: student.ra,
              name: student.name,
              photo: student.photo,
              contacts: student.contacts,
              classId: classId || student.classId || null
            },
            { allowDemoFallback: demoFallback }
          ),
          guardiansLoaded: true
        };
        students = [enriched];
        fromStudent = true;
        selectedRas = [enriched.ra];
        selectedGuardianIdsByRa = {
          [enriched.ra]: (enriched.guardians || []).map(g => g.id)
        };
        // Aluno + responsáveis presentes por padrão (operador pode desmarcar)
        studentPresentByRa = { [enriched.ra]: true };
        title = this.suggestMeetingTitle(selectedRas, [enriched]);
      } else if (classId) {
        // Lista da turma não traz contacts — responsáveis só após carregar o perfil
        const raw = await TeacherService.getStudentsForClass(classId);
        students = (raw || []).map(s =>
          enrichStudentWithGuardians(s, { allowDemoFallback: false })
        );
      }

      const operator = this.buildOperatorStaff();
      let staffFromApi = [];
      // Só funcionários vinculados à turma — sem classId não lista a escola inteira
      if (classId) {
        try {
          // VW_FUNCIONARIO_TURMA: professores + coordenadores da turma
          const teachers = await TeacherService.getTeachers(
            "",
            classId,
            "PROFESSOR,COORDENADOR"
          );
          staffFromApi = (teachers || [])
            .slice(0, 20)
            .map((teacher, idx) => mapTeacherToStaff(teacher, idx))
            .filter(p => p.name && !/^Funcionário(\s+\d+)?$/i.test(p.name));
        } catch (e) {
          staffFromApi = [];
        }
      }

      // Fallback demo offline — em LB auth usamos só a API + operador
      const staffFallback =
        demoFallback && classId ? buildDemoStaffForClass(classId) : [];

      const staff = mergeStaffLists(operator, staffFromApi, staffFallback);
      // Todos os funcionários presentes por padrão (desmarcar quem não participa)
      const selectedStaffIds = staff.map(s => s.id);

      this.setState({
        students,
        staff,
        selectedRas,
        selectedGuardianIdsByRa,
        studentPresentByRa,
        selectedStaffIds,
        classId: classId || null,
        fromStudent,
        title,
        titleTouched: false,
        priorHistoryOpenById: {}
      });
    } catch (e) {
      console.error(e);
      alert.show(t("smartMinutes.new.loadParticipantsError"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  buildOperatorStaff = () => {
    const { t } = this.props;
    const name = (UserStore.getUserName() || "").trim();
    const externalId = UserStore.getUserExternalId() || "me";
    return {
      id: `operator-${externalId}`,
      externalId: String(externalId),
      name: name || t("smartMinutes.new.operator"),
      role: t("smartMinutes.role.teacherOperator")
    };
  };

  /**
   * Lista da turma não traz contacts.resp — busca o perfil ao selecionar.
   */
  ensureGuardiansLoaded = async ra => {
    const { students } = this.state;
    const student = students.find(s => s.ra === ra);
    if (!student) return;
    if ((student.guardians || []).length > 0 || student.guardiansLoaded) {
      return;
    }

    const profileId = student.ra || student.id || ra;

    try {
      const profile = await StudentService.getStudentById(profileId);
      const contacts =
        profile.contacts ||
        (Array.isArray(profile.responsaveis)
          ? { resp: profile.responsaveis, respTransport: [] }
          : null);
      const enriched = enrichStudentWithGuardians(
        {
          ...student,
          contacts
        },
        { allowDemoFallback: isClassDiaryDemo() }
      );
      this.setState(prev => {
        const nextStudents = prev.students.map(s =>
          s.ra === ra ? { ...enriched, guardiansLoaded: true } : s
        );
        const patch = { students: nextStudents };
        // Se o aluno já está na ata, traz todos os responsáveis como presentes
        if (prev.selectedRas.includes(ra)) {
          const allIds = (enriched.guardians || []).map(g => g.id);
          const current = prev.selectedGuardianIdsByRa[ra] || [];
          if (!current.length && allIds.length) {
            patch.selectedGuardianIdsByRa = {
              ...prev.selectedGuardianIdsByRa,
              [ra]: allIds
            };
          }
          if (prev.studentPresentByRa[ra] !== false) {
            patch.studentPresentByRa = {
              ...prev.studentPresentByRa,
              [ra]: true
            };
          }
        }
        return patch;
      });
    } catch (e) {
      this.setState(prev => ({
        students: prev.students.map(s =>
          s.ra === ra ? { ...s, guardiansLoaded: true } : s
        )
      }));
    }
  };

  toggleStudent = ra => {
    const {
      selectedRas,
      selectedGuardianIdsByRa,
      studentPresentByRa,
      fromStudent
    } = this.state;
    if (fromStudent) return;

    if (selectedRas.includes(ra)) {
      const nextGuardians = { ...selectedGuardianIdsByRa };
      const nextPresent = { ...studentPresentByRa };
      delete nextGuardians[ra];
      delete nextPresent[ra];
      this.setState(
        {
          selectedRas: selectedRas.filter(r => r !== ra),
          selectedGuardianIdsByRa: nextGuardians,
          studentPresentByRa: nextPresent,
          priorHistoryOpenById: {}
        },
        this.syncSuggestedTitle
      );
      return;
    }

    this.setState(
      {
        selectedRas: [...selectedRas, ra],
        selectedGuardianIdsByRa: {
          ...selectedGuardianIdsByRa,
          [ra]: selectedGuardianIdsByRa[ra] || []
        },
        studentPresentByRa: {
          ...studentPresentByRa,
          [ra]: true
        },
        priorHistoryOpenById: {}
      },
      () => {
        this.ensureGuardiansLoaded(ra);
        this.syncSuggestedTitle();
      }
    );
  };

  selectAllStudents = () => {
    const {
      students,
      selectedGuardianIdsByRa,
      studentPresentByRa
    } = this.state;
    const nextGuardians = { ...selectedGuardianIdsByRa };
    const nextPresent = { ...studentPresentByRa };
    students.forEach(s => {
      if (!nextGuardians[s.ra]) nextGuardians[s.ra] = [];
      if (nextPresent[s.ra] === undefined) nextPresent[s.ra] = true;
    });
    this.setState(
      {
        selectedRas: students.map(s => s.ra),
        selectedGuardianIdsByRa: nextGuardians,
        studentPresentByRa: nextPresent,
        priorHistoryOpenById: {}
      },
      () => {
        students.forEach(s => this.ensureGuardiansLoaded(s.ra));
        this.syncSuggestedTitle();
      }
    );
  };

  clearStudents = () => {
    this.setState(
      {
        selectedRas: [],
        selectedGuardianIdsByRa: {},
        studentPresentByRa: {},
        priorHistoryOpenById: {}
      },
      this.syncSuggestedTitle
    );
  };

  ensureStudentSelected = ra => {
    const {
      selectedRas,
      selectedGuardianIdsByRa,
      studentPresentByRa
    } = this.state;
    if (selectedRas.includes(ra)) {
      return {
        selectedRas,
        selectedGuardianIdsByRa,
        studentPresentByRa,
        newlySelected: false
      };
    }
    return {
      selectedRas: [...selectedRas, ra],
      selectedGuardianIdsByRa: {
        ...selectedGuardianIdsByRa,
        [ra]: selectedGuardianIdsByRa[ra] || []
      },
      studentPresentByRa: {
        ...studentPresentByRa,
        [ra]: true
      },
      newlySelected: true
    };
  };

  toggleStudentPresent = ra => {
    const next = this.ensureStudentSelected(ra);
    this.setState(
      {
        selectedRas: next.selectedRas,
        selectedGuardianIdsByRa: next.selectedGuardianIdsByRa,
        studentPresentByRa: {
          ...next.studentPresentByRa,
          [ra]: !next.studentPresentByRa[ra]
        }
      },
      () => {
        if (next.newlySelected) this.ensureGuardiansLoaded(ra);
      }
    );
  };

  toggleGuardian = (ra, guardianId) => {
    const { fromStudent, selectedRas } = this.state;
    const next = this.ensureStudentSelected(ra);
    const current = next.selectedGuardianIdsByRa[ra] || [];
    const nextIds = current.includes(guardianId)
      ? current.filter(id => id !== guardianId)
      : [...current, guardianId];

    this.setState(
      {
        selectedRas: fromStudent ? selectedRas : next.selectedRas,
        selectedGuardianIdsByRa: {
          ...next.selectedGuardianIdsByRa,
          [ra]: nextIds
        },
        studentPresentByRa: next.studentPresentByRa
      },
      () => {
        if (next.newlySelected) this.ensureGuardiansLoaded(ra);
      }
    );
  };

  /** Marca/desmarca família na conversa: aluno presente + todos os responsáveis. */
  selectFamilyPresent = async ra => {
    await this.ensureGuardiansLoaded(ra);
    const {
      students,
      fromStudent,
      selectedRas,
      studentPresentByRa
    } = this.state;
    const student = students.find(s => s.ra === ra);
    if (!student) return;
    const allIds = (student.guardians || []).map(g => g.id);
    const next = this.ensureStudentSelected(ra);
    const current = next.selectedGuardianIdsByRa[ra] || [];
    const studentPresent = studentPresentByRa[ra] === true;
    const allGuardiansSelected =
      allIds.length === 0 || allIds.every(id => current.includes(id));
    const familyPresent = studentPresent && allGuardiansSelected;
    this.setState({
      selectedRas: fromStudent ? selectedRas : next.selectedRas,
      selectedGuardianIdsByRa: {
        ...next.selectedGuardianIdsByRa,
        [ra]: familyPresent ? [] : allIds
      },
      studentPresentByRa: {
        ...next.studentPresentByRa,
        [ra]: !familyPresent
      }
    });
  };

  toggleStaff = id => {
    const { selectedStaffIds } = this.state;
    this.setState({
      selectedStaffIds: selectedStaffIds.includes(id)
        ? selectedStaffIds.filter(x => x !== id)
        : [...selectedStaffIds, id]
    });
  };

  selectAllStaff = () => {
    this.setState(prev => ({
      selectedStaffIds: prev.staff.map(s => s.id)
    }));
  };

  clearStaff = () => {
    this.setState({ selectedStaffIds: [] });
  };

  onPickFile = event => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    this.setState({
      audioFile: file,
      audioFileName: file.name,
      entryMode: ENTRY_UPLOAD
    });
  };

  buildSelectedStudents = () => {
    const { t } = this.props;
    const {
      students,
      selectedRas,
      selectedGuardianIdsByRa,
      studentPresentByRa
    } = this.state;
    return students
      .filter(s => selectedRas.includes(s.ra))
      .map(s => {
        const selectedIds = selectedGuardianIdsByRa[s.ra] || [];
        const selectedGuardians = (s.guardians || []).filter(g =>
          selectedIds.includes(g.id)
        );
        const present = studentPresentByRa[s.ra] === true;
        return {
          ra: s.ra,
          name: s.name,
          present,
          guardians: selectedGuardians.map(g => ({
            id: g.id,
            name: g.name,
            role: g.role,
            responsibility: g.responsibility || null,
            relationship: g.relationship || null
          })),
          guardiansLabel: selectedGuardians.length
            ? selectedGuardians.map(formatGuardianChipLabel).join("; ")
            : t("smartMinutes.new.noGuardiansSelected")
        };
      });
  };

  buildSelectedStaff = () => {
    const { staff, selectedStaffIds } = this.state;
    return staff
      .filter(s => selectedStaffIds.includes(s.id))
      .map(s => ({ id: s.id, name: s.name, role: s.role }));
  };

  studentsSummary = () => {
    const { t } = this.props;
    const selected = this.buildSelectedStudents();
    if (!selected.length) return t("smartMinutes.new.noStudentSelected");
    return selected
      .map(s => {
        const presence = s.present
          ? t("smartMinutes.presence.present")
          : t("smartMinutes.presence.absent");
        const guardians = s.guardians || [];
        const guardianPart = guardians.length
          ? guardians.map(g => `${g.role}: ${g.name}`).join(", ")
          : t("smartMinutes.new.withoutGuardians");
        return `${s.name} (${presence}; ${guardianPart})`;
      })
      .join(" · ");
  };

  staffSummary = () => {
    const { t } = this.props;
    const selected = this.buildSelectedStaff();
    if (!selected.length) return t("smartMinutes.new.noStaffSelected");
    return selected.map(s => `${s.name} — ${s.role}`).join(" · ");
  };

  listPath = () => {
    const { match } = this.props;
    if (match.params.studentId) {
      return `/student/${match.params.studentId}/ata-inteligente`;
    }
    if (match.params.id) {
      return `/classes/${match.params.id}/ata-inteligente`;
    }
    return "/classes";
  };

  suggestMeetingTitle = (ras, studentsOverride) => {
    const { t } = this.props;
    const { selectedRas: stateRas, students: stateStudents } = this.state;
    const selectedRas = ras || stateRas || [];
    const students =
      studentsOverride ||
      (stateStudents || []).filter(s => selectedRas.includes(s.ra));
    if (!selectedRas.length || !students.length) {
      return t("smartMinutes.new.defaultTitle");
    }
    const priorCount = listPriorAtasForStudents(selectedRas).length;
    let subject = students[0].name;
    if (students.length === 2) {
      subject = t("smartMinutes.new.andStudents", {
        first: students[0].name,
        second: students[1].name
      });
    } else if (students.length > 2) {
      subject = t("smartMinutes.new.studentsMore", {
        first: students[0].name,
        count: students.length - 1
      });
    }
    if (priorCount >= 2) {
      return t("smartMinutes.new.followUpTitle", { subject });
    }
    if (priorCount === 1) {
      return t("smartMinutes.new.returnTitle", { subject });
    }
    return t("smartMinutes.ataTitle", { subject });
  };

  syncSuggestedTitle = () => {
    const { titleTouched } = this.state;
    if (titleTouched) return;
    this.setState({ title: this.suggestMeetingTitle() });
  };

  getPriorContext = () => {
    const { selectedRas } = this.state;
    const priorAtas = listPriorAtasForStudents(selectedRas);
    const allActions = collectActionsFromAtas(priorAtas, { openOnly: false });
    const openActions = allActions.filter(isActionOpen);
    const inProgressActions = openActions.filter(a => {
      const status = getActionStatus(a);
      return (
        status === ACTION_STATUS.IN_PROGRESS || status === ACTION_STATUS.OVERDUE
      );
    });
    const doneCount = allActions.filter(
      a => getActionStatus(a) === ACTION_STATUS.DONE
    ).length;
    const cancelledCount = allActions.filter(
      a => getActionStatus(a) === ACTION_STATUS.CANCELLED
    ).length;
    return {
      priorAtas,
      allActions,
      openActions,
      inProgressActions,
      doneCount,
      cancelledCount
    };
  };

  isPriorMeetingExpanded = (ataId, index) => {
    const { priorHistoryOpenById } = this.state;
    if (Object.prototype.hasOwnProperty.call(priorHistoryOpenById, ataId)) {
      return !!priorHistoryOpenById[ataId];
    }
    return index === 0;
  };

  togglePriorMeeting = ataId => {
    this.setState(prev => {
      const priorAtas = listPriorAtasForStudents(prev.selectedRas);
      const index = priorAtas.findIndex(a => a.id === ataId);
      const currentlyOpen = Object.prototype.hasOwnProperty.call(
        prev.priorHistoryOpenById,
        ataId
      )
        ? !!prev.priorHistoryOpenById[ataId]
        : index === 0;
      return {
        priorHistoryOpenById: {
          ...prev.priorHistoryOpenById,
          [ataId]: !currentlyOpen
        }
      };
    });
  };

  submit = async () => {
    const { alert, history, match, t } = this.props;
    const {
      entryMode,
      title,
      meetingAt,
      notes,
      audioFileName,
      audioFile,
      classId,
      selectedRas,
      students: rosterStudents,
      staff: rosterStaff,
      submitting
    } = this.state;

    if (submitting) {
      return;
    }

    if (!selectedRas.length) {
      alert.show(t("smartMinutes.new.selectStudents"), {
        title: t("common.actions.attention")
      });
      return;
    }

    if (entryMode === ENTRY_UPLOAD && !audioFile) {
      alert.show(t("smartMinutes.new.needAudio"), {
        title: t("common.actions.attention")
      });
      return;
    }

    if (!meetingAt) {
      alert.show(t("smartMinutes.new.needDatetime"), {
        title: t("common.actions.attention")
      });
      return;
    }

    const isAudio = entryMode === ENTRY_UPLOAD;
    const students = this.buildSelectedStudents();
    const staff = this.buildSelectedStaff();
    // Pool completo: quem não foi marcado na criação ainda pode ser
    // adicionado depois na revisão de falas (cenário comum na gravação).
    const staffPool = (rosterStaff || []).map(s => ({
      id: s.id,
      name: s.name,
      role: s.role
    }));
    const guardiansPoolByRa = {};
    selectedRas.forEach(ra => {
      const student = (rosterStudents || []).find(s => s.ra === ra);
      if (!student) return;
      guardiansPoolByRa[ra] = (student.guardians || []).map(g => ({
        id: g.id,
        name: g.name,
        role: g.role,
        responsibility: g.responsibility || null,
        relationship: g.relationship || null
      }));
    });
    // Áudio: encaminhamentos vêm do job e são liberados automaticamente.
    // Texto: ainda pode nascer com sugestões locais.
    const actions = isAudio
      ? []
      : buildSuggestedActions({
          notes,
          students,
          staff,
          processedByAi: false
        });
    const priorMeetingContext = buildPriorMeetingContext(selectedRas);
    const resolvedClassId = classId || match.params.id || null;
    const hasPrior = !!(priorMeetingContext.priorAtaIds || []).length;
    const defaultTitle = this.suggestMeetingTitle(selectedRas, students);
    let draft = {
      id: createDraftId(),
      createdAt: new Date().toISOString(),
      status: ATA_STATUS.RASCUNHO,
      entryMode,
      title: title || defaultTitle,
      meetingAt: meetingAt || null,
      notes: notes || (hasPrior ? t("smartMinutes.new.familyReturnHint") : ""),
      audioFileName: audioFileName || null,
      audioSize: audioFile ? audioFile.size : null,
      hasLocalAudio: Boolean(isAudio && audioFile),
      classId: resolvedClassId,
      students,
      staff,
      staffPool,
      guardiansPoolByRa,
      actions,
      pendingActions: null,
      actionsReady: !isAudio && actions.length > 0,
      priorMeetingContext,
      voicesLinked: false,
      transcriptCorrected: false,
      actionsValidated: false,
      processedByAi: false,
      processPhase: isAudio ? "queued" : null,
      transcriptText: isAudio ? t("smartMinutes.new.processingNote") : null,
      willCreateActivity: true
    };

    this.setState({ submitting: true });
    try {
      if (isAudio && audioFile) {
        await saveAtaAudio(draft.id, audioFile);
      }
      // Local primeiro; upsert await evita race de create duplicado
      saveAtaDraft(draft, { persist: false });
      const synced = await syncAtaDraftToServer(draft);
      const localAfterSync = getAtaDraft(draft.id);
      const meetingId =
        (synced && synced.meetingId) ||
        (localAfterSync && localAfterSync.meetingId) ||
        null;
      if (meetingId) {
        draft = { ...draft, meetingId };
        saveAtaDraft(draft, { persist: false });
      }

      if (isAudio) {
        let audioBase64 = null;
        if (audioFile) {
          audioBase64 = await fileToBase64(audioFile);
        }
        await SmartMinutesService.processSmartMinutesJob({
          draftId: draft.id,
          meetingId: meetingId || undefined,
          classId: resolvedClassId,
          title: draft.title,
          audioFileName: draft.audioFileName,
          audioSize: draft.audioSize,
          audioMime: (audioFile && audioFile.type) || "audio/mpeg",
          audioBase64,
          meetingAt: draft.meetingAt,
          notes: draft.notes,
          entryMode,
          students,
          staff,
          // Contexto da reunião anterior (fechamento de encaminhamentos no retorno)
          priorMeetingContext: draft.priorMeetingContext || null
        });
        await fetchGradeJobQueue();
      }

      history.push(this.listPath());
    } catch (e) {
      console.error(e);
      alert.show(t("smartMinutes.new.processError"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  renderEntryDetails = () => {
    const { entryMode, audioFileName } = this.state;
    const { t } = this.props;

    if (entryMode !== ENTRY_UPLOAD) {
      return null;
    }

    return (
      <Box mt={1}>
        <input
          ref={this.fileInputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.ogg"
          style={{ display: "none" }}
          onChange={this.onPickFile}
        />
        <FilePickButton
          color="primary"
          variant="contained"
          startIcon={<AttachFileIcon />}
          onClick={() =>
            this.fileInputRef.current && this.fileInputRef.current.click()}
        >
          {t("smartMinutes.new.chooseAudio")}
        </FilePickButton>
        {audioFileName && (
          <Typography
            variant="body2"
            style={{ marginTop: 10, color: "#455a64" }}
          >
            {t("smartMinutes.new.fileSelected")}
            {" "}
            <strong>{audioFileName}</strong>
          </Typography>
        )}
      </Box>
    );
  };

  renderPriorActionsPanel = () => {
    const { t } = this.props;
    const {
      priorAtas,
      allActions,
      openActions,
      inProgressActions,
      doneCount,
      cancelledCount
    } = this.getPriorContext();

    if (!allActions.length) {
      return null;
    }

    const selectedStudents = this.buildSelectedStudents();
    const ataForGrouping = { students: selectedStudents, actions: openActions };
    const pendingCount = openActions.length - inProgressActions.length;
    const summaryParts = [
      inProgressActions.length
        ? t("smartMinutes.new.inProgressPartial", {
            count: inProgressActions.length
          })
        : null,
      pendingCount > 0
        ? t("smartMinutes.new.pendingCount", { count: pendingCount })
        : null,
      doneCount > 0
        ? t("smartMinutes.new.doneCount", { count: doneCount })
        : null,
      cancelledCount > 0
        ? t("smartMinutes.new.cancelledCount", { count: cancelledCount })
        : null
    ].filter(Boolean);

    return (
      <StickySideCard>
        <Group>
          <TitleRow>
            <AssignmentIcon
              style={{ color: appTheme.color.primary.dark, fontSize: 26 }}
            />
            <Heading level={2} gutterBottom={false}>
              {t("smartMinutes.new.actionsTitle")}
            </Heading>
            <Chip size="small" label={String(allActions.length)} />
          </TitleRow>
          <HistoryMeta style={{ marginTop: 6 }}>
            {t("smartMinutes.new.openActionsHighlight")}
          </HistoryMeta>

          <PriorActionsScroll>
            {openActions.length > 0 ? (
              <>
                <FocusLabel>
                  {t("smartMinutes.new.openLabel")}
                  {inProgressActions.length
                    ? t("smartMinutes.new.partialProgress", {
                        count: inProgressActions.length
                      })
                    : ""}
                </FocusLabel>
                <AtaActionsGrouped
                  ata={ataForGrouping}
                  actions={openActions}
                  showStatus
                  showOrigin={false}
                />
              </>
            ) : (
              <HistoryMeta style={{ marginTop: 12 }}>
                {t("smartMinutes.new.noOpenActions")}
              </HistoryMeta>
            )}

            {summaryParts.length > 0 && (
              <SummaryLineActions>
                {t("smartMinutes.new.historySummary")}
                {" "}
                {summaryParts.join(" · ")}
              </SummaryLineActions>
            )}

            <HistorySectionLabel>
              {t("smartMinutes.new.previousMeetings")}
            </HistorySectionLabel>
            {priorAtas.map((ata, index) => {
              const meetingActions = collectActionsFromAtas([ata], {
                openOnly: false
              });
              if (!meetingActions.length) return null;
              const open = this.isPriorMeetingExpanded(ata.id, index);
              const when = moment(ata.meetingAt || ata.createdAt).isValid()
                ? moment(ata.meetingAt || ata.createdAt).format(
                    "DD/MM/YYYY HH:mm"
                  )
                : "";
              return (
                <div key={ata.id}>
                  <MeetingToggle
                    type="button"
                    aria-expanded={open}
                    onClick={() => this.togglePriorMeeting(ata.id)}
                  >
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <MeetingToggleTitle>
                      {ata.title || t("smartMinutes.ata")}
                    </MeetingToggleTitle>
                    {when ? (
                      <MeetingToggleWhen>{when}</MeetingToggleWhen>
                    ) : null}
                    <MeetingToggleCount>
                      {meetingActions.length}
                    </MeetingToggleCount>
                  </MeetingToggle>
                  <Collapse in={open}>
                    <Box pl={0.5} pb={1.5}>
                      <AtaActionsGrouped
                        ata={{
                          students: ata.students || selectedStudents,
                          actions: meetingActions
                        }}
                        actions={meetingActions}
                        showStatus
                        showOrigin={false}
                      />
                    </Box>
                  </Collapse>
                </div>
              );
            })}
          </PriorActionsScroll>
        </Group>
      </StickySideCard>
    );
  };

  renderStudentSection = () => {
    const { t } = this.props;
    const {
      students,
      selectedRas,
      selectedGuardianIdsByRa,
      studentPresentByRa,
      studentsExpanded,
      fromStudent
    } = this.state;

    return (
      <Group>
        <SectionHeader
          type="button"
          onClick={() =>
            this.setState(prev => ({
              studentsExpanded: !prev.studentsExpanded
            }))}
          aria-expanded={studentsExpanded}
        >
          <Box flex="1 1 auto" minWidth={0}>
            <Heading level={2} gutterBottom={false}>
              {t("smartMinutes.new.studentsSection")}
            </Heading>
            <SummaryLine>
              {studentsExpanded
                ? t("smartMinutes.new.studentsHelp")
                : this.studentsSummary()}
            </SummaryLine>
            {studentsExpanded && selectedRas.length > 0 && (
              <SummaryLine style={{ marginTop: 2 }}>
                <strong>{t("smartMinutes.new.selectedLabel")}</strong>
                {" "}
                {this.studentsSummary()}
              </SummaryLine>
            )}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Chip
              size="small"
              color="primary"
              label={t("smartMinutes.new.studentCount", {
                count: selectedRas.length
              })}
            />
            <IconButton
              size="small"
              aria-label={t("smartMinutes.new.toggleStudents")}
            >
              {studentsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </SectionHeader>

        <Collapse in={studentsExpanded}>
          <Box mt={2}>
            {!fromStudent && (
              <BulkActions>
                <BulkButton type="button" onClick={this.selectAllStudents}>
                  {t("smartMinutes.new.selectAll")}
                </BulkButton>
                <BulkButton
                  type="button"
                  $tone="clear"
                  onClick={this.clearStudents}
                >
                  {t("smartMinutes.new.clearSelected")}
                </BulkButton>
              </BulkActions>
            )}

            {!students.length && (
              <Typography color="textSecondary">
                {t("smartMinutes.new.noStudentsInClass")}
              </Typography>
            )}

            {students.map(student => {
              const selected = selectedRas.includes(student.ra);
              const studentPresent = studentPresentByRa[student.ra] === true;
              const selectedGuardianIds =
                selectedGuardianIdsByRa[student.ra] || [];
              const guardians = student.guardians || [];
              const allGuardiansSelected =
                guardians.length === 0 ||
                guardians.every(g => selectedGuardianIds.includes(g.id));
              const familyPresent = studentPresent && allGuardiansSelected;

              return (
                <StudentRow key={student.ra} $selected={selected}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{ gap: 8 }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{
                        gap: 10,
                        cursor: fromStudent ? "default" : "pointer"
                      }}
                      onClick={() => this.toggleStudent(student.ra)}
                    >
                      {!fromStudent && (
                        <Checkbox
                          color="primary"
                          checked={selected}
                          onChange={() => this.toggleStudent(student.ra)}
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                      <Box>
                        <Typography variant="subtitle1">
                          <strong>{student.name}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("common.labels.ra")} 
                          {' '}
                          {student.ra}
                          {selected &&
                            ` · ${
                              studentPresent
                                ? t("smartMinutes.new.inConversation")
                                : t("smartMinutes.new.outConversation")
                            }`}
                        </Typography>
                      </Box>
                    </Box>
                    {selected && (
                      <BulkButton
                        type="button"
                        $tone={familyPresent ? "clear" : undefined}
                        onClick={e => {
                          e.stopPropagation();
                          this.selectFamilyPresent(student.ra);
                        }}
                      >
                        {familyPresent
                          ? t("smartMinutes.new.clearSelected")
                          : t("smartMinutes.new.familyPresent")}
                      </BulkButton>
                    )}
                  </Box>

                  {selected && (
                    <Box
                      mt={1.25}
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: 8 }}
                    >
                      <GuardianChip
                        clickable
                        color={studentPresent ? "primary" : "default"}
                        variant={studentPresent ? "default" : "outlined"}
                        label={t("smartMinutes.new.studentPresent")}
                        onClick={() => this.toggleStudentPresent(student.ra)}
                      />
                      {guardians.map(g => {
                        const active = selectedGuardianIds.includes(g.id);
                        return (
                          <GuardianChip
                            key={g.id}
                            clickable
                            color={active ? "primary" : "default"}
                            variant={active ? "default" : "outlined"}
                            label={formatGuardianChipLabel(g)}
                            onClick={() =>
                              this.toggleGuardian(student.ra, g.id)}
                          />
                        );
                      })}
                      {!guardians.length && student.guardiansLoaded && (
                        <Typography variant="body2" color="textSecondary">
                          {t("smartMinutes.new.noGuardians")}
                        </Typography>
                      )}
                      {!guardians.length && !student.guardiansLoaded && (
                        <Typography variant="body2" color="textSecondary">
                          {t("smartMinutes.new.loadingGuardians")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </StudentRow>
              );
            })}
          </Box>
        </Collapse>
      </Group>
    );
  };

  renderStaffSection = () => {
    const { t } = this.props;
    const { staff, selectedStaffIds, staffExpanded } = this.state;

    return (
      <Group>
        <SectionHeader
          type="button"
          onClick={() =>
            this.setState(prev => ({ staffExpanded: !prev.staffExpanded }))}
          aria-expanded={staffExpanded}
        >
          <Box flex="1 1 auto" minWidth={0}>
            <Heading level={2} gutterBottom={false}>
              {t("smartMinutes.new.staffSection")}
            </Heading>
            <SummaryLine>
              {staffExpanded
                ? t("smartMinutes.new.staffHelp")
                : this.staffSummary()}
            </SummaryLine>
            {staffExpanded && selectedStaffIds.length > 0 && (
              <SummaryLine style={{ marginTop: 2 }}>
                <strong>{t("smartMinutes.new.selectedLabel")}</strong>
                {" "}
                {this.staffSummary()}
              </SummaryLine>
            )}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Chip
              size="small"
              color="primary"
              label={t("smartMinutes.new.staffCount", {
                count: selectedStaffIds.length
              })}
            />
            <IconButton
              size="small"
              aria-label={t("smartMinutes.new.toggleStaff")}
            >
              {staffExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </SectionHeader>

        <Collapse in={staffExpanded}>
          <Box mt={2}>
            <BulkActions>
              <BulkButton type="button" onClick={this.selectAllStaff}>
                {t("smartMinutes.new.selectAll")}
              </BulkButton>
              <BulkButton type="button" $tone="clear" onClick={this.clearStaff}>
                {t("smartMinutes.new.clearSelected")}
              </BulkButton>
            </BulkActions>

            {!staff.length && (
              <Typography color="textSecondary">
                {t("smartMinutes.new.noStaffInClass")}
              </Typography>
            )}

            {staff.map(person => {
              const selected = selectedStaffIds.includes(person.id);
              return (
                <StudentRow key={person.id} $selected={selected}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{ gap: 8 }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: 10, cursor: "pointer" }}
                      onClick={() => this.toggleStaff(person.id)}
                    >
                      <Checkbox
                        color="primary"
                        checked={selected}
                        onChange={() => this.toggleStaff(person.id)}
                        onClick={e => e.stopPropagation()}
                      />
                      <Box>
                        <Typography variant="subtitle1">
                          <strong>{person.name}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {person.role}
                          {selected
                            ? ` · ${t("smartMinutes.new.participates")}`
                            : ""}
                        </Typography>
                      </Box>
                    </Box>
                    {selected && (
                      <BulkButton
                        type="button"
                        $tone="clear"
                        onClick={e => {
                          e.stopPropagation();
                          this.toggleStaff(person.id);
                        }}
                      >
                        {t("common.actions.remove")}
                      </BulkButton>
                    )}
                  </Box>

                  {selected && (
                    <Box
                      mt={1.25}
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: 8 }}
                    >
                      <GuardianChip
                        clickable
                        color="primary"
                        variant="default"
                        label={t("smartMinutes.new.participatesChip", {
                          role: person.role || t("smartMinutes.new.staff")
                        })}
                        onClick={() => this.toggleStaff(person.id)}
                      />
                    </Box>
                  )}
                </StudentRow>
              );
            })}
          </Box>
        </Collapse>
      </Group>
    );
  };

  render() {
    const { t } = this.props;
    const {
      loading,
      entryMode,
      title,
      notes,
      meetingAt,
      submitting
    } = this.state;

    if (loading) {
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );
    }

    const priorPanel = this.renderPriorActionsPanel();
    const hasPriorSide = !!priorPanel;

    let submitLabel = t("smartMinutes.new.scheduleDemo");
    if (submitting) {
      submitLabel = t("smartMinutes.new.sending");
    } else if (entryMode === ENTRY_UPLOAD) {
      submitLabel = t("smartMinutes.new.submit");
    }

    return (
      <Grid container spacing={2}>
        <BackButton to={this.listPath()} />

        <Grid item xs={12}>
          <FormSplit $hasSide={hasPriorSide}>
            <Group>
              <TitleRow>
                <MicIcon
                  style={{ color: appTheme.color.primary.dark, fontSize: 36 }}
                />
                <Heading level={1} gutterBottom={false}>
                  {t("smartMinutes.new.title")}
                </Heading>
                <NewFeatureBadge />
              </TitleRow>
              <Typography
                variant="body1"
                style={{ marginTop: 8, color: "#555" }}
              >
                {t("smartMinutes.new.intro")}
                {hasPriorSide ? t("smartMinutes.new.introWithPrior") : ""}
              </Typography>

              <Box mt={3}>
                <Heading level={2} gutterBottom>
                  {t("smartMinutes.new.howToRecord")}
                </Heading>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <EntryCard
                      type="button"
                      $active={entryMode === ENTRY_UPLOAD}
                      onClick={() => this.setState({ entryMode: ENTRY_UPLOAD })}
                    >
                      <CloudUploadIcon />
                      <Typography variant="subtitle1">
                        <strong>{t("smartMinutes.new.upload")}</strong>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t("smartMinutes.new.uploadHelp")}
                      </Typography>
                    </EntryCard>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <EntryCard
                      type="button"
                      $active={entryMode === ENTRY_MEET}
                      onClick={() => this.setState({ entryMode: ENTRY_MEET })}
                    >
                      <VideoCallIcon />
                      <Typography variant="subtitle1">
                        <strong>
                          {t("smartMinutes.entryMode.google_meet")}
                        </strong>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t("smartMinutes.new.meetOnline")}
                      </Typography>
                    </EntryCard>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <EntryCard
                      type="button"
                      $active={entryMode === ENTRY_PRESENCIAL}
                      onClick={() =>
                        this.setState({ entryMode: ENTRY_PRESENCIAL })}
                    >
                      <MeetingRoomIcon />
                      <Typography variant="subtitle1">
                        <strong>
                          {t("smartMinutes.entryMode.presencial")}
                        </strong>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t("smartMinutes.new.presencialHelp")}
                      </Typography>
                    </EntryCard>
                  </Grid>
                </Grid>
                {this.renderEntryDetails()}
              </Box>

              <SoftDivider />

              <Box mt={1} mb={1}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={7}>
                    <TextField
                      label={t("smartMinutes.new.meetingTitle")}
                      fullWidth
                      margin="normal"
                      value={title}
                      onChange={e =>
                        this.setState({
                          title: e.target.value,
                          titleTouched: true
                        })}
                      helperText={t("smartMinutes.new.titleHelp")}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      label={t("smartMinutes.new.datetime")}
                      type="datetime-local"
                      fullWidth
                      margin="normal"
                      value={meetingAt}
                      onChange={e =>
                        this.setState({ meetingAt: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      /* Reserva a mesma altura do helperText do título (alinha baselines). */
                      helperText={"\u00a0"}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box mt={2}>
                <TextField
                  label={t("smartMinutes.new.agenda")}
                  fullWidth
                  multiline
                  rows={3}
                  value={notes}
                  onChange={e => this.setState({ notes: e.target.value })}
                />
              </Box>

              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={this.submit}
                  disabled={submitting}
                  startIcon={
                    entryMode === ENTRY_UPLOAD && !submitting ? (
                      <MemoryIcon />
                    ) : (
                      undefined
                    )
                  }
                >
                  {submitLabel}
                </Button>
              </Box>
            </Group>

            {priorPanel}
          </FormSplit>
        </Grid>

        <Grid item xs={12}>
          {this.renderStudentSection()}
        </Grid>

        <Grid item xs={12}>
          {this.renderStaffSection()}
        </Grid>
      </Grid>
    );
  }
}

SmartMinutes.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      studentId: PropTypes.string
    })
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(SmartMinutes)));
